// Library utilities
import moment from "moment";
import { addLeadingZero } from "../../../../utils/date-format";

// Hooks
import React, { useState, useEffect } from "react";

// Styles
import Styles from "./transactionStats.module.scss";
import sharedStyles from "../dashboard.module.scss";
import { ACCENTCOLOR } from "../../../../Shared/colors";

// Components
import Switch from "../../../../Shared/toggle-switch/switch";
import CustomDatePicker from "../../../../Shared/custom-date-picker/customDatePicker";
import InputDropdown from "../../../../Shared/input-dropdown/inputDropdown";
import Loading from "../../../../Shared/loading/loading";

// Constants
import { durationOptions } from "../../../../constants/data";
import { envTypes } from "../../../../constants/envTypes";

// Functions
import { callApi } from "../../../../api/fetch";
import { ensureArray, isTestApp } from "../../../../utils/dashboardMappers";
import DataTable from "../../../../Shared/DataTable/DataTable";

function pickDefaultApp(appDetails: any, appData: any[]) {
  const apps = Array.isArray(appData) ? appData : [];
  if (isTestApp(appDetails)) return appDetails;
  return apps.find((app) => isTestApp(app)) ?? appDetails ?? apps[0] ?? null;
}

function TransactionStats({ loading, appDetails, appData }: any) {
  const org_id = localStorage.getItem("organisation_id");
  // const user: any = localStorage.getItem("@user");
  // const x_user_id = JSON.parse(user)._id;

  const [activePageNumber, setActivePageNumber] = useState(1);
  const [totalNumberOfPages, setTotalNumberOfPages] = useState(0);
  const [selectedApp, setSelectedApp] = useState(() =>
    pickDefaultApp(appDetails, appData)
  );

  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  // Dummy data for testing
  // const dummyTransactionStats = [
  //   {
  //     product: "in_identity_pan_lite",
  //     date: { value: "2026-03-05" },
  //     billable_txn_count: 15,
  //     success_txn_count: 12,
  //     failure_txn_count: 3,
  //     product_name: "Pan Lite",
  //   },
  //   {
  //     product: "in_identity_pan_advance",
  //     date: { value: "2026-03-05" },
  //     billable_txn_count: 25,
  //     success_txn_count: 23,
  //     failure_txn_count: 2,
  //     product_name: "Pan Advance",
  //   },
  //   {
  //     product: "in_identity_aadhaar",
  //     date: { value: "2026-03-04" },
  //     billable_txn_count: 42,
  //     success_txn_count: 40,
  //     failure_txn_count: 2,
  //     product_name: "Aadhaar Verification",
  //   },
  //   {
  //     product: "in_identity_gstin",
  //     date: { value: "2026-03-04" },
  //     billable_txn_count: 8,
  //     success_txn_count: 8,
  //     failure_txn_count: 0,
  //     product_name: "GSTIN Verification",
  //   },
  //   {
  //     product: "in_identity_driving_license",
  //     date: { value: "2026-03-04" },
  //     billable_txn_count: 18,
  //     success_txn_count: 17,
  //     failure_txn_count: 1,
  //     product_name: "Driving License",
  //   },
  //   {
  //     product: "in_identity_voter_id",
  //     date: { value: "2026-03-03" },
  //     billable_txn_count: 33,
  //     success_txn_count: 30,
  //     failure_txn_count: 3,
  //     product_name: "Voter ID",
  //   },
  //   {
  //     product: "in_identity_passport",
  //     date: { value: "2026-03-03" },
  //     billable_txn_count: 12,
  //     success_txn_count: 11,
  //     failure_txn_count: 1,
  //     product_name: "Passport Verification",
  //   },
  //   {
  //     product: "in_bank_account_verification",
  //     date: { value: "2026-03-03" },
  //     billable_txn_count: 56,
  //     success_txn_count: 54,
  //     failure_txn_count: 2,
  //     product_name: "Bank Account Verification",
  //   },
  //   {
  //     product: "in_identity_rc",
  //     date: { value: "2026-03-02" },
  //     billable_txn_count: 28,
  //     success_txn_count: 27,
  //     failure_txn_count: 1,
  //     product_name: "RC Verification",
  //   },
  //   {
  //     product: "in_identity_cin",
  //     date: { value: "2026-03-02" },
  //     billable_txn_count: 9,
  //     success_txn_count: 9,
  //     failure_txn_count: 0,
  //     product_name: "CIN Verification",
  //   },
  // ];

  const [transactionStats, setTransactionStats] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState("Last 30 days");
  const [customDurationRange, setCustomDurationRange] = useState({
    to: {
      day: 0,
      month: 0,
      year: 0,
    },
    from: {
      day: 0,
      month: 0,
      year: 0,
    },
  });
  async function getTransactionStats() {
    let str = `&app_id=${selectedApp?.app_id}&env=${
      selectedApp?.test_app ? envTypes.TEST : envTypes.PRODUCTION
    }`;
    const end_date = moment(new Date()).format("YYYY-MM-DD");
    switch (selectedDuration) {
      case "Last 7 days": {
        const last_seventh_date = new Date(
          moment().subtract(7, "days").calendar()
        );
        str += `&start_date=${moment(last_seventh_date).format(
          "YYYY-MM-DD"
        )}&end_date=${moment(end_date).format("YYYY-MM-DD")}`;
        break;
      }
      case "Last 30 days": {
        const last_thirtieth_date = new Date(
          moment().subtract(30, "days").calendar()
        );
        str += `&start_date=${moment(last_thirtieth_date).format(
          "YYYY-MM-DD"
        )}&end_date=${moment(end_date).format("YYYY-MM-DD")}`;
        break;
      }
      case "Last 60 days": {
        const last_sixtieth_date = new Date(
          moment().subtract(60, "days").calendar()
        );
        str += `&start_date=${moment(last_sixtieth_date).format(
          "YYYY-MM-DD"
        )}&end_date=${moment(end_date).format("YYYY-MM-DD")}`;
        break;
      }
      case "Last 120 days": {
        const last_quarter_date = new Date(
          moment().subtract(120, "days").calendar()
        );
        str += `&start_date=${moment(last_quarter_date).format(
          "YYYY-MM-DD"
        )}&end_date=${moment(end_date).format("YYYY-MM-DD")}`;
        break;
      }
      case "Custom": {
        if (!customDurationRange.from.day && !customDurationRange.to.day)
          return;
        str += `&start_date=${`${
          customDurationRange.from.year
        }-${addLeadingZero(customDurationRange.from.month)}-${addLeadingZero(
          customDurationRange.from.day
        )}`}&end_date=${`${customDurationRange.to.year}-${addLeadingZero(
          customDurationRange.to.month
        )}-${addLeadingZero(customDurationRange.to.day)}`}`;
        break;
      }
    }
    const query = str;
    setActivePageNumber(1);
    try {
      const res = await callApi(
        `/organisations/${org_id}/analytics/transaction-stats?${query.replace(
          /^&/,
          ""
        )}`,
        {
          method: "GET",
          init: {},
        }
      );

      return setTransactionStats(ensureArray(res));
    } catch (err) {
      console.log(err);
    }
    return;
  }

  useEffect(() => {
    if (!Array.isArray(appData) || appData.length === 0) return;
    setSelectedApp((current: any) => {
      if (current && appData.some((app: any) => app.app_id === current.app_id)) {
        return current;
      }
      return pickDefaultApp(appDetails, appData);
    });
  }, [appData, appDetails]);

  useEffect(() => {
    if (!selectedApp) return;
    if (selectedDuration === "Custom" && showCustomDatePicker) return;
    getTransactionStats();
  }, [selectedDuration, selectedApp, customDurationRange]);

  const tableHeader = [
    {
      name: "Product Name",
      className: Styles.name,
    },
    // {
    //   name: "Balance Credits",
    //   className: Styles.name,
    // },
    {
      name: "Billable Transactions",
      className: Styles.transNo,
    },
    {
      name: "Success",
      className: Styles.status,
    },
    {
      name: "Failed",
      className: Styles.resCode,
    },
  ];

  const tableHeaderCustom = (
    <div className="d-flex justify-content-between align-items-center bg-white px-2 py-2 gap-2" style={{ minWidth: 0 }}>
      <p
        style={{
          fontSize: "18px",
          fontWeight: 600,
          color: "#000",
          flexShrink: 0,
        }}
        className={"mb-0"}
      >
        Transaction Stats
      </p>
      <div className="mt-2">
        <div className={`d-flex align-items-center ${Styles.duration}`}>
          <div className="mx-2">
            <Switch
              positiveLabel="Production"
              negativeLabel="Trial"
              purpose="forDashboard"
              id="dashboard-transaction-stats-env-switch"
              checked={!isTestApp(selectedApp)}
              disabled={true}
            />
          </div>
          <div className="d-flex flex-grow-1 gap-2" style={{ minWidth: 0 }}>
            <div
              className="d-flex flex-column mr-3"
              style={{ minWidth: 0, width: 160, maxWidth: 160 }}
            >
              <p className={sharedStyles.dropDownHeader}>Duration</p>
              <InputDropdown
                label="Duration"
                id="dashboard-transaction-stats-duration"
                style={{ width: "100%", maxWidth: "100%" }}
                no_shadow="true"
                placeholder="Last 30 days"
                optionsArray={durationOptions}
                click={(value: string) => {
                  setSelectedDuration(value);
                  if (value === "Custom") {
                    setShowCustomDatePicker(true);
                  }
                }}
              />
            </div>
            <div
              className="d-flex flex-column mr-3"
              style={{ minWidth: 0, width: 160, maxWidth: 160 }}
            >
              <p className={sharedStyles.dropDownHeader}>Apps</p>
              <InputDropdown
                label="Apps"
                style={{ width: "100%", maxWidth: "100%" }}
                optionsArray={appData.map((app: any) => app.app_name)}
                placeholder={selectedApp?.app_name || appData[0]?.app_name}
                default_value={selectedApp?.app_name || ""}
                no_shadow="true"
                id="dashboard-transaction-stats-app-select"
                click={(value: string) => {
                  const temp = appData.find((app: any) => app.app_name === value);
                  setSelectedApp(temp);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {showCustomDatePicker && (
        <CustomDatePicker
          setShowCustomDatePicker={setShowCustomDatePicker}
          maxDateDifference={120}
          setCustomDurationRange={(data: any) => {
            setCustomDurationRange(data);
            setShowCustomDatePicker(false);
          }}
        />
      )}
      <div className={Styles.transactionTable}>
        {loading ? (
          <div
            style={{ height: "80vh" }}
            className="d-flex align-items-center justify-content-center"
          >
            <Loading loadingColor={ACCENTCOLOR} />
          </div>
        ) : (
          <DataTable
            headers={
              isTestApp(selectedApp)
                ? tableHeader
                : tableHeader.filter(
                    (header) => header.name !== "Balance Credits"
                  )
            }
            loading={loading}
            rows={transactionStats}
            setActivePageNumber={setActivePageNumber}
            totalNumberOfPages={totalNumberOfPages}
            tableHeader={tableHeaderCustom}
            bodyClassName={Styles.transactionTableBody}
            purpose="forDashboard"
          >
            {transactionStats?.length ? (
              transactionStats.map((traxn: any, index: number) => {
                return (
                  <tr key={`${index} ${Styles.tableRow}`}>
                    <td className="px-4 py-2">
                      <p className={"mb-0 body_small_text"}>
                        {traxn.product_name}
                      </p>
                    </td>
                    {/* {selectedApp?.is_test_app && (
                      <td className="px-4 py-2">
                        <p className={"mb-0 body_small_text"}>
                          {traxn.test_credits}
                        </p>
                      </td>
                    )} */}
                    <td className="px-4 py-2 text-center">
                      <p className={"mb-0 body_small_text"}>
                        {traxn.billable_txn_count}
                      </p>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <p className={"mb-0 body_small_text"}>
                        {traxn.success_txn_count}
                      </p>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <p className={"mb-0 body_small_text"}>
                        {traxn.failure_txn_count}
                      </p>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={4} className="text-center py-4">
                  <div className="d-flex justify-content-center align-items-center">
                    No data to display
                  </div>
                </td>
              </tr>
            )}
          </DataTable>
        )}
      </div>
    </>
  );
}

export default TransactionStats;
